import { SuspenseSelect } from '@/components/Suspense';
import { useGetInvoiceReportsPaymentStatuses } from '@/context/services/invoice_reports/InvoiceReportSearch.service';
import { InvoiceReportsSearchFormData } from '@/features/invoice_reports/components/Search/hooks/useSearchForm';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  FormSelect,
  IconHelp,
  SelectProps,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import { SearchInvoiceReportsQueryPaymentStatus } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import { Control, FieldPath } from 'react-hook-form';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

type PaymentStatusSelectProps = {
  control: Control<InvoiceReportsSearchFormData>;
  name: FieldPath<InvoiceReportsSearchFormData>;
  placeholder?: string;
};

const _PaymentStatusSelect: FC<PaymentStatusSelectProps> = memo(
  ({ control, name, placeholder }) => {
    const { t } = useTranslation();
    const container = useGlobalContainerRef();
    const { data } = useGetInvoiceReportsPaymentStatuses();

    const memoizedPaymentStatusOptions = useMemo(() => {
      return (
        data?.data?.map((status) => ({
          label: status.display_name ?? '',
          value: status.value ?? '',
        })) ?? []
      );
    }, [data?.data]);

    const getTooltipTitle = useCallback(
      (value: string) => {
        switch (value) {
          case SearchInvoiceReportsQueryPaymentStatus.not_yet_paid:
            return t(
              'invoice_reports.search.payment_status.not_yet_paid_tooltip'
            );
          case SearchInvoiceReportsQueryPaymentStatus.paid:
            return t('invoice_reports.search.payment_status.paid_tooltip');
          default:
            return '';
        }
      },
      [t]
    );

    const paymentStatusOptions = useMemo(() => {
      const options = memoizedPaymentStatusOptions.map((option) => ({
        ...option,
        render: (
          <div className={cx('option-container')}>
            <span>{option.label}</span>
            <Tooltip
              title={getTooltipTitle(option.value)}
              placement='right'
              getPopupContainer={container}
            >
              <div
                className={cx('icon-container')}
                onClick={(e) => e.stopPropagation()}
              >
                <IconHelp size={14} />
              </div>
            </Tooltip>
          </div>
        ),
      }));
      return options;
    }, [memoizedPaymentStatusOptions, getTooltipTitle, container]);

    const selectProps: SelectProps = useMemo(
      () => ({
        options: paymentStatusOptions,
        isAllowClear: true,
        size: 'small',
        placeholder: placeholder ?? '',
      }),
      [paymentStatusOptions, placeholder]
    );

    return (
      <FormSelect<InvoiceReportsSearchFormData>
        control={control}
        name={name}
        inputId={name}
        testId={name}
        selectProps={selectProps}
      />
    );
  }
);

_PaymentStatusSelect.displayName = '_PaymentStatusSelect';

export const PaymentStatusSelect: FC<PaymentStatusSelectProps> = memo(
  (props) => (
    <SuspenseSelect>
      <_PaymentStatusSelect {...props} />
    </SuspenseSelect>
  )
);

PaymentStatusSelect.displayName = 'PaymentStatusSelect';
